import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/card';
import { Input } from './ui/input';
import { ThemeToggle } from './ui/theme-toggle';
import { useTheme } from '../contexts/ThemeContext';
import heic2any from 'heic2any';
import { ScrollArea } from "./ui/scroll-area"
import { Separator } from "./ui/separator"
import ExifReader from 'exifreader';
import { AlertTriangle, CheckCircle, Upload, Smartphone, Calendar, MapPin, Camera, HelpCircle, Eraser, Download, Dices, Repeat2, X, Check, Loader2, Edit2 } from 'lucide-react'; // Added Check icon
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

const getFieldsToRemove = () => [
  'gps', 'location', 'date', 'time', 'creator',
  'copyright', 'serial_number', 'device_id',
  'user_comment', 'artist', 'author', 'owner_name',
  'camera_id', 'unique_camera_model', 'camera_serial_number',
  'image_unique_id', 'original_file_name', 'internal_serial_number',
  'make', 'model', 'software', 'lens_make', 'lens_model',
  'exposure_time', 'f_number', 'iso', 'focal_length',
  'flash', 'exposure_mode', 'white_balance', 'digital_zoom_ratio'
];

const AnonPix = () => {
  const { theme } = useTheme();
  const [file, setFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [removedMetadata, setRemovedMetadata] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isHeic, setIsHeic] = useState(false);
  const fileInputRef = useRef(null);
  const [hasIdentifyingMetadata, setHasIdentifyingMetadata] = useState(false);
  const [convertMenuOpen, setConvertMenuOpen] = useState(false);
  const [convertTo, setConvertTo] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [originalFormat, setOriginalFormat] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isConvertingPreview, setIsConvertingPreview] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState('');

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        if (file.type === 'image/heic') {
          setIsConvertingPreview(true);
          try {
            const heicBlob = new Blob([reader.result], { type: 'image/heic' });
            const jpegBlob = await heic2any({
              blob: heicBlob,
              toType: 'image/jpeg',
              quality: 0.8
            });
            setPreviewImage(URL.createObjectURL(jpegBlob));
          } catch (error) {
            console.error('Error converting HEIC to JPEG:', error);
            setPreviewImage(null);
          } finally {
            setIsConvertingPreview(false);
          }
        } else {
          setPreviewImage(URL.createObjectURL(file));
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }, [file]);

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 25 * 1024 * 1024) {
        setError('File size exceeds 25MB limit');
        return;
      }
      setFile(selectedFile);
      setIsHeic(selectedFile.type === 'image/heic');
      setError(null);
      setProcessedImage(null);
      setRemovedMetadata(null);
      setConvertTo(null);

      // Check for identifying metadata
      try {
        const arrayBuffer = await selectedFile.arrayBuffer();
        const tags = await ExifReader.load(arrayBuffer);
        
        const hasIdentifying = getFieldsToRemove().some(field => 
          Object.keys(tags).some(key => key.toLowerCase().includes(field.toLowerCase()))
        );
        
        setHasIdentifyingMetadata(hasIdentifying);
      } catch (error) {
        console.error('Error checking metadata:', error);
        setHasIdentifyingMetadata(false);
      }

      const format = selectedFile.type.split('/')[1].toUpperCase();
      setOriginalFormat(format === 'JPEG' ? 'JPG' : format);
    }
  };

  const removeMetadata = useCallback(async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setIsLoading(true);
    try {
      let blob;
      let removedData = {};

      // Read the metadata from the original file
      const arrayBuffer = await file.arrayBuffer();
      const tags = await ExifReader.load(arrayBuffer);

      console.log('Metadata being removed:');
      let hasIdentifying = false;
      for (const [key, value] of Object.entries(tags)) {
        if (getFieldsToRemove().some(field => key.toLowerCase().includes(field.toLowerCase()))) {
          const metadataValue = value.description || value.value;
          console.log(`${key}: ${JSON.stringify(metadataValue)}`);
          removedData[key] = metadataValue;
          hasIdentifying = true;
        }
      }

      setHasIdentifyingMetadata(hasIdentifying);

      // For HEIC, just use the original file without conversion
      blob = file;

      if (blob) {
        const url = URL.createObjectURL(blob);
        setProcessedImage({
          url: url,
          type: blob.type,
          name: file.name
        });

        setRemovedMetadata({
          name: file.name,
          originalSize: file.size,
          processedSize: blob.size,
          type: blob.type,
          lastModified: new Date().toISOString(),
          totalFieldsWiped: Object.keys(removedData).length,
          removedData
        });
      } else {
        throw new Error('Failed to process image');
      }
    } catch (error) {
      console.error('Error processing image:', error);
      setError('Error processing image. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [file]);

  const createCleanImage = async (imageFile) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(resolve, imageFile.type, 0.8);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(imageFile);
    });
  };

  const handleDownload = () => {
    if (processedImage) {
      const a = document.createElement('a');
      a.href = processedImage.url;
      a.download = processedImage.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Add this function to categorize metadata
  const categorizeMetadata = (key, value) => {
    const deviceFields = ['Make', 'Model', 'Software', 'DeviceModelNumber', 'ProfileCreator', 'ICCCopyright'];
    const dateTimeFields = ['DateTime', 'DateTimeOriginal', 'DateTimeDigitized', 'OffsetTime', 'OffsetTimeOriginal', 'OffsetTimeDigitized', 'SubSecTimeOriginal', 'SubSecTimeDigitized', 'GPSDateStamp', 'ICCProfileDate'];
    const locationFields = ['GPSInfo', 'GPSLatitude', 'GPSLongitude', 'GPSAltitude', 'GPSSpeed', 'GPSImgDirection', 'GPSDestBearing', 'GPSHPositioningError'];
    const cameraFields = ['ExposureTime', 'ISOSpeedRatings', 'Flash', 'LensMake', 'LensModel'];

    if (deviceFields.some(field => key.includes(field))) return 'Device';
    if (dateTimeFields.some(field => key.includes(field))) return 'DateTime';
    if (locationFields.some(field => key.includes(field))) return 'Location';
    if (cameraFields.some(field => key.includes(field))) return 'Camera';
    return 'Misc';
  };

  // Add this function to get the appropriate icon for each category
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Device':
        return <Smartphone className="w-3 h-3 mr-1" />;
      case 'DateTime':
        return <Calendar className="w-3 h-3 mr-1" />;
      case 'Location':
        return <MapPin className="w-3 h-3 mr-1" />;
      case 'Camera':
        return <Camera className="w-3 h-3 mr-1" />;
      default:
        return <HelpCircle className="w-3 h-3 mr-1" />;
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreviewImage(null);
    setProcessedImage(null);
    setRemovedMetadata(null);
    setHasIdentifyingMetadata(false);
    // Reset any other relevant state
  };

  const handleConvert = async (format) => {
    if (!processedImage) return;

    if (format === originalFormat) {
      setConvertTo(null);
      return;
    }

    if (convertTo === format) {
      setConvertTo(null);
      return;
    }

    setIsConverting(true);
    setError(null);

    try {
      let blob;
      if (isHeic && format.toLowerCase() !== 'heic') {
        // Convert HEIC to desired format
        blob = await heic2any({
          blob: file,
          toType: `image/${format.toLowerCase()}`,
          quality: 0.8
        });
      } else if (!isHeic && format.toLowerCase() === 'heic') {
        // Converting to HEIC is not supported in the browser
        throw new Error("Converting to HEIC is not supported.");
      } else {
        // For other conversions, use canvas
        const response = await fetch(processedImage.url);
        const imageBlob = await response.blob();
        const image = await createImageBitmap(imageBlob);

        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0);

        blob = await new Promise(resolve => canvas.toBlob(resolve, `image/${format.toLowerCase()}`));
      }

      if (!blob) {
        throw new Error("Failed to convert image.");
      }

      const newUrl = URL.createObjectURL(blob);

      setProcessedImage(prevState => ({
        ...prevState,
        url: newUrl,
        type: `image/${format.toLowerCase()}`,
        name: prevState.name.replace(/\.[^/.]+$/, `.${format.toLowerCase()}`)
      }));
      setConvertTo(format);
    } catch (error) {
      console.error('Error converting image:', error);
      setError(`Error converting image: ${error.message}`);
      setConvertTo(null);
    } finally {
      setIsConverting(false);
    }
  };

  const getDownloadButtonText = () => {
    const actionText = hasIdentifyingMetadata ? "Scrubbed" : "Clean";
    const formatText = convertTo ? convertTo.toLowerCase() : (isHeic ? 'HEIC' : originalFormat.toLowerCase());
    return `Download ${actionText} ${formatText}`;
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange({ target: { files: e.dataTransfer.files } });
    }
  };

  const handleEditField = (key, value) => {
    setEditingField(key);
    setEditedValue(JSON.stringify(value));
  };

  const handleSaveEdit = () => {
    if (editingField) {
      try {
        const parsedValue = JSON.parse(editedValue);
        setRemovedMetadata(prevState => ({
          ...prevState,
          removedData: {
            ...prevState.removedData,
            [editingField]: parsedValue
          }
        }));
        setEditingField(null);
      } catch (error) {
        console.error('Invalid JSON:', error);
        // Optionally, you can show an error message to the user here
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    }
  };

  return (
    <div className={`min-h-screen bg-background text-foreground flex flex-col ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="flex-grow flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-8">
          <div className="absolute top-4 right-4"><ThemeToggle /></div>
          <header className="text-center">
            <h1 className="text-4xl font-bold mb-2 font-['Departure_Mono']">
              <span className="text-green-400">Anon</span>
              <span className="text-green-600">Pix</span>
              <span className="text-foreground"> Converter</span>
            </h1>
            <p className="text-muted-foreground">
              Remove metadata from images securely and locally.
            </p>
          </header>

          {file ? (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Upload className="w-5 h-5 mr-2" />
                      Image Uploaded
                    </div>
                    <Button
                      onClick={handleRemoveFile}
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                  <CardDescription>Basic file information</CardDescription>
                </CardHeader>
                <CardContent className="flex space-x-4">
                  <div className="w-1/3">
                    {isConvertingPreview ? (
                      <div className="w-full h-full flex items-center justify-center border border-input rounded-md bg-background/50">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    ) : previewImage ? (
                      <img 
                        src={previewImage} 
                        alt="Preview" 
                        className="w-full h-auto object-cover rounded-md"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center border border-input rounded-md bg-background/50">
                        <span className="text-muted-foreground">No preview available</span>
                      </div>
                    )}
                  </div>
                  <div className="w-2/3 space-y-1.5 text-xs font-['Departure_Mono']">
                    {hasIdentifyingMetadata && (
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-200 text-red-800 mb-1">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Identifying Information Detected
                      </span>
                    )}
                    <div><span className="font-medium">Name:</span> {file.name}</div>
                    <div><span className="font-medium">Size:</span> {(file.size / 1024 / 1024).toFixed(2)} MB</div>
                    <div><span className="font-medium">Type:</span> {file.type}</div>
                    <div><span className="font-medium">Last Modified:</span> {new Date(file.lastModified).toLocaleString()}</div>
                  </div>
                </CardContent>
              </Card>

              {removedMetadata && !isLoading ? (
                <>
                  {removedMetadata.totalFieldsWiped > 0 ? (
                    <>
                      <div className="flex items-center p-2 bg-red-100 border border-red-400 rounded-md text-red-700">
                        <AlertTriangle className="w-5 h-5 mr-2" />
                        <span className="text-sm font-medium">
                          {removedMetadata.totalFieldsWiped} identifying metadata fields wiped
                        </span>
                      </div>
                      <ScrollArea className="h-[300px] w-full rounded-md border">
                        <div className="p-4">
                          <h3 className="font-semibold mb-4">Removed Metadata</h3>
                          {Object.entries(removedMetadata.removedData).map(([key, value], index, array) => {
                            const category = categorizeMetadata(key, value);
                            return (
                              <div key={key}>
                                <div className="mb-2 flex items-center group">
                                  <div className="flex-grow mr-2 text-xs font-['Departure_Mono'] overflow-hidden">
                                    <span className="font-medium">{key}:</span>
                                    {editingField === key ? (
                                      <input
                                        type="text"
                                        value={editedValue}
                                        onChange={(e) => setEditedValue(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        className="ml-2 p-1 border rounded flex-grow min-w-0 bg-background text-foreground dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                                        style={{ width: 'calc(100% - 4rem)' }}
                                        autoFocus
                                      />
                                    ) : (
                                      <span className="ml-2 inline-block">{JSON.stringify(value)}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-2 flex-shrink-0">
                                    {editingField === key ? (
                                      <Button
                                        onClick={handleSaveEdit}
                                        variant="ghost"
                                        size="sm"
                                        className="p-1 hover:bg-accent hover:text-accent-foreground transition-colors duration-200 ease-in-out rounded-none"
                                      >
                                        <Check className="h-4 w-4 text-green-500" />
                                      </Button>
                                    ) : (
                                      <Button
                                        onClick={() => handleEditField(key, value)}
                                        variant="ghost"
                                        size="sm"
                                        className="p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent hover:text-accent-foreground transition-colors duration-200 ease-in-out rounded-none"
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                                      category === 'Device' ? 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200' :
                                      category === 'DateTime' ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' :
                                      category === 'Location' ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                                      category === 'Camera' ? 'bg-purple-200 text-purple-800 dark:bg-purple-800 dark:text-purple-200' :
                                      'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                    }`}>
                                      {getCategoryIcon(category)}
                                      {category}
                                    </span>
                                  </div>
                                </div>
                                {index < array.length - 1 && <Separator className="my-2" />}
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </>
                  ) : (
                    <div className="flex items-center p-2 bg-transparent border border-green-400 rounded-md text-green-700">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      <span className="text-sm font-medium">
                        No identifying metadata fields found
                      </span>
                    </div>
                  )}
                  <div className="flex space-x-2">
                    <Button 
                      onClick={handleDownload} 
                      className="flex-grow bg-background text-foreground border border-input hover:bg-accent hover:text-accent-foreground"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {getDownloadButtonText()}
                    </Button>
                    <div className="relative group">
                      <Button
                        className="p-2 aspect-square bg-background text-foreground border border-input hover:bg-accent hover:text-accent-foreground"
                        onClick={() => {/* Add dice functionality here */}}
                      >
                        <Dices className="w-4 h-4" />
                      </Button>
                      <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs font-bold text-white bg-gray-700 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Randomize
                      </span>
                    </div>
                    <div className="relative group">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className={`p-2 aspect-square ${convertTo ? 'bg-accent text-accent-foreground' : 'bg-background text-foreground'} border border-input hover:bg-accent hover:text-accent-foreground`}
                            disabled={isConverting}
                          >
                            {isConverting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat2 className="w-4 h-4" />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="top" align="center">
                          <p className="p-2">Convert</p>
                          {originalFormat === 'PNG' && (
                            <DropdownMenuItem onSelect={() => handleConvert('PNG')} disabled={isConverting}>
                              Keep as PNG {(convertTo === 'PNG' || !convertTo) && <Check className="ml-2 h-4 w-4" />}
                            </DropdownMenuItem>
                          )}
                          {originalFormat !== 'PNG' && (
                            <DropdownMenuItem onSelect={() => handleConvert('PNG')} disabled={isConverting}>
                              Convert to PNG {convertTo === 'PNG' && <Check className="ml-2 h-4 w-4" />}
                            </DropdownMenuItem>
                          )}
                          {originalFormat === 'JPG' && (
                            <DropdownMenuItem onSelect={() => handleConvert('JPEG')} disabled={isConverting}>
                              Keep as JPG {(convertTo === 'JPEG' || !convertTo) && <Check className="ml-2 h-4 w-4" />}
                            </DropdownMenuItem>
                          )}
                          {originalFormat !== 'JPG' && (
                            <DropdownMenuItem onSelect={() => handleConvert('JPEG')} disabled={isConverting}>
                              Convert to JPG {convertTo === 'JPEG' && <Check className="ml-2 h-4 w-4" />}
                            </DropdownMenuItem>
                          )}
                          {originalFormat === 'HEIC' && (
                            <DropdownMenuItem onSelect={() => handleConvert('HEIC')} disabled={isConverting}>
                              Keep as HEIC {(convertTo === 'HEIC' || !convertTo) && <Check className="ml-2 h-4 w-4" />}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs font-bold text-white bg-gray-700 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Convert
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <Button 
                  onClick={removeMetadata} 
                  className="w-full bg-background text-foreground border border-input hover:bg-accent hover:text-accent-foreground"
                  disabled={isLoading}
                >
                  <Eraser className="w-4 h-4 mr-2" />
                  {isLoading ? 'Processing...' : 'Remove Metadata'}
                </Button>
              )}
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Upload Image</CardTitle>
                <CardDescription>Choose an image to remove its metadata.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className={`border border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary bg-primary/10'
                      : 'border-muted-foreground'
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <p className="text-muted-foreground mb-4 pointer-events-none">
                    {isDragging ? 'Drop the image here' : 'Drag and drop an image or click to upload'}
                  </p>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".heic,.png,.jpeg,.jpg"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer pointer-events-none">
                    <Button 
                      variant="outline" 
                      type="button" 
                      onClick={() => fileInputRef.current.click()}
                      className="pointer-events-auto"
                    >
                      Choose File
                    </Button>
                  </label>
                </div>

                <Button 
                  onClick={removeMetadata} 
                  className="w-full"
                  disabled={!file || isLoading}
                  variant="outline"
                >
                  <Eraser className="w-4 h-4 mr-2" />
                  Scrub Metadata
                </Button>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {isLoading && (
                  <div className="text-center text-muted-foreground animate-pulse">
                    Removing metadata...
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <section className="mt-12">
            <h2 className="text-2xl font-bold text-center mb-6">How we remove metadata</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                { 
                  title: 'Upload', 
                  description: "Select an image file from your device. Our tool supports various formats including JPEG, PNG, and HEIC.",
                  icon: Upload
                },
                { 
                  title: 'Anonymize', 
                  description: "We remove or replace identifying metadata. This includes GPS data, device information, and other sensitive details.",
                  icon: Eraser
                },
                { 
                  title: 'Convert', 
                  description: "Optionally convert to a different image format. This can help further obfuscate the original file's properties.",
                  icon: Repeat2
                },
                { 
                  title: 'Enjoy', 
                  description: "Download your processed, metadata-free image. Your privacy is protected, and the image quality remains intact.",
                  icon: Download
                }
              ].map((step, index) => (
                <Card key={step.title}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <step.icon className="w-5 h-5 mr-2" />
                      {step.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>

      <footer className="bg-gray-100 dark:bg-gray-800 py-6 mt-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-center md:text-left mb-4 md:mb-0">
              <p className="text-sm text-gray-600 dark:text-gray-400">© 2024 AnonPix. All rights reserved.</p>
            </div>
            <div className="flex space-x-4">
              <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">Privacy Policy</a>
              <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">Terms of Service</a>
              <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AnonPix;
